import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

type AlertType =
  | "devtools_open"
  | "copy_attempt"
  | "right_click_spam"
  | "view_source_attempt"
  | "print_attempt"
  | "multiple_signals";

type Severity = "low" | "medium" | "high";

interface AlertPayload {
  alert_type: AlertType;
  severity?: Severity;
  page_url?: string;
  session_id?: string;
  details?: Record<string, unknown>;
}

function getSessionId(): string {
  try {
    const key = "aw_session_id";
    let id = sessionStorage.getItem(key);
    if (!id) {
      id =
        (crypto as Crypto & { randomUUID?: () => string }).randomUUID?.() ??
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(key, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

/**
 * Watches the visitor for "suspicious" signals — devtools open, repeated
 * right-clicks, copy/print attempts, view-source shortcuts — and reports
 * them to the `log-security-alert` edge function.
 *
 * Each signal is reported at most once per session. Multiple signals in one
 * session escalate to severity "high" + alert_type "multiple_signals".
 *
 * IMPORTANT: this is a deterrent / audit log only. Determined attackers can
 * always copy a public website's HTML — there is no JS-only way to truly
 * prevent that. Combine with the visible copyright notice in the footer.
 */
export function useSecurityWatcher() {
  // Use ref so the cleanup closure always sees the latest set.
  const firedRef = useRef<Set<AlertType>>(new Set());
  const sessionIdRef = useRef<string>("");
  const rightClickCountRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    sessionIdRef.current = getSessionId();

    const fire = async (payload: AlertPayload) => {
      if (firedRef.current.has(payload.alert_type)) return;
      firedRef.current.add(payload.alert_type);

      try {
        await supabase.functions.invoke("log-security-alert", {
          body: {
            alert_type: payload.alert_type,
            severity: payload.severity ?? "low",
            page_url: window.location.href.slice(0, 2048),
            session_id: sessionIdRef.current,
            details: payload.details ?? {},
          },
        });
      } catch (err) {
        // Silent — never let logging break the page
        if (import.meta.env.DEV) console.warn("[security] alert failed", err);
      }

      // Two or more signals in one session → escalate
      if (firedRef.current.size === 2 && !firedRef.current.has("multiple_signals")) {
        firedRef.current.add("multiple_signals");
        try {
          await supabase.functions.invoke("log-security-alert", {
            body: {
              alert_type: "multiple_signals",
              severity: "high",
              page_url: window.location.href.slice(0, 2048),
              session_id: sessionIdRef.current,
              details: { signals: Array.from(firedRef.current) },
            },
          });
        } catch {
          /* swallow */
        }
      }
    };

    // 1. Right-click spam (3+ in 10s) — single right-click is normal, ignore.
    const onContextMenu = () => {
      rightClickCountRef.current += 1;
      if (rightClickCountRef.current >= 3) {
        fire({
          alert_type: "right_click_spam",
          severity: "low",
          details: { count: rightClickCountRef.current },
        });
      }
      // Reset window after 10s
      window.setTimeout(() => {
        rightClickCountRef.current = Math.max(0, rightClickCountRef.current - 1);
      }, 10000);
    };

    // 2. Copy attempt
    const onCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection?.()?.toString() ?? "";
      // Ignore tiny accidental copies
      if (selection.length < 30) return;
      fire({
        alert_type: "copy_attempt",
        severity: "medium",
        details: { length: selection.length },
      });
    };

    // 3. View-source / save shortcuts
    const onKeyDown = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      const cmd = e.ctrlKey || e.metaKey;
      // Ctrl+U (view source), Ctrl+S (save page), F12 (devtools)
      if (cmd && k === "u") {
        fire({ alert_type: "view_source_attempt", severity: "medium", details: { combo: "Ctrl+U" } });
      } else if (cmd && k === "s") {
        fire({ alert_type: "view_source_attempt", severity: "medium", details: { combo: "Ctrl+S" } });
      } else if (k === "f12") {
        fire({ alert_type: "devtools_open", severity: "medium", details: { trigger: "F12" } });
      } else if (cmd && e.shiftKey && (k === "i" || k === "j" || k === "c")) {
        fire({ alert_type: "devtools_open", severity: "medium", details: { trigger: `Ctrl+Shift+${k.toUpperCase()}` } });
      } else if (cmd && k === "p") {
        fire({ alert_type: "print_attempt", severity: "low", details: {} });
      }
    };

    // 4. Devtools detection via the size-diff trick (inexact but widely used).
    // Skip in dev so we don't fire on ourselves.
    let devtoolsCheck: number | null = null;
    if (!import.meta.env.DEV) {
      const threshold = 160;
      devtoolsCheck = window.setInterval(() => {
        const widthDiff = window.outerWidth - window.innerWidth;
        const heightDiff = window.outerHeight - window.innerHeight;
        if (widthDiff > threshold || heightDiff > threshold) {
          fire({
            alert_type: "devtools_open",
            severity: "high",
            details: { widthDiff, heightDiff, trigger: "size_check" },
          });
        }
      }, 4000);
    }

    // 5. Print
    const onBeforePrint = () =>
      fire({ alert_type: "print_attempt", severity: "low", details: { trigger: "print_event" } });

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("copy", onCopy);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("beforeprint", onBeforePrint);

    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("beforeprint", onBeforePrint);
      if (devtoolsCheck !== null) window.clearInterval(devtoolsCheck);
    };
  }, []);
}